import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { getDisplayNameForNodeId } from '@opengov/component-library/capital';
import { Map } from '@opengov/compose-app/immutable';
import { useChatBotSnippet } from '@opengov/bnp-salesforce-chatbot';

import {
  DEFAULT_PER_PAGE,
  PATH_CREATE_FROM_TEMPLATE,
  PUBLICATION_VISIBILITY,
  SHOW,
  SHOW_ALL,
  SHOW_USER,
  SORT,
  SORT_CUSTOM,
  SORT_UPDATED_NEW_TO_OLD,
  STATE_ALL,
  STATE_PUBLISHED,
  STORIES_ADMIN_PRIVILEGE,
  STORY_VISIBILITY,
  TAB_ANALYTICS,
  TAB_PUBLICATIONS,
  TAB_STORIES,
  TAB_TEMPLATES,
  VIEW,
  VIEW_LIST
} from '@opengov/stories-shared/constants';
import { IStory } from '@opengov/stories-shared/types';
import { debounce, omit } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Alert from 'react-s-alert';
import Cookies from 'universal-cookie';

import {
  EProcessStatus,
  ESelectAllStatus,
  IExportPdfProcess,
  IFolderCopyProcess,
  IModalMessages,
  IPublicationListMessages,
  IPublicationModalMessages,
  IPublicationViewMessages,
  ISessionUser,
  ISortOptions,
  IStoriesCopyProcess,
  IStoriesFolder,
  IStoryCookie,
  IStoryListing,
  IStoryListMessages,
  IStoryListSelection,
  IStoryShowOptions,
  IStoryViewOptions,
  IStoryVisibilityOptions,
  ITabOptions
} from '../../../../types';
import { fetchAllPublicationsInEntity } from '../hooks/fetchAllPublicationsInEntity';
import {
  useCurrentSubjectClaims,
  useCurrentUserGroupsIds
} from '../../../hooks/subjectClaims';
import { useFetchSettings } from '../../StoriesSettings/hooks/useFetchSettings';
import { EMPTY_SETTINGS } from '../../StoriesSettings/emptySettings';
import { useGetAllPublications } from '../../Publications/PublicationList/hooks/useGetAllPublications';
import {
  useFoldersAndSubFoldersStoryCount,
  usePublicationFoldersTree,
  useStories,
  useStoryFolders,
  useStoryListAnalytics,
  useStoryListRoute,
  useTemplates
} from '../hooks';
import {
  folderCopyProcessesSubscriptionQuery,
  getExportPdfDetailsForFolder,
  storiesCopyProcessesSubscriptionQuery
} from '../../../graphQL/queries';
import { navigateToFolderList, navigateToPublicationList, navigateToSectionSetupPDF } from '../../../state/navigation';
import {
  alphabetizeNodes,
  compileNodes,
  compilePublicationNodes,
  getFolderById,
  getNewCookieValue,
  getPathForFolder,
  hasPrivilege,
  HasuraSubscription,
  isTopLevelNode,
  LONG_POLL_INTERVAL_SHORT,
  MessageListener
} from '../../../utils';
import { PublicationList } from '../../Publications/PublicationList/PublicationList';
import { Flash } from '../../StoriesPage';
import { ControlBar } from '../ControlBar';
import { CopyFolderModal } from '../CopyFolderModal';
import { DeleteFolderModal } from '../DeleteFolderModal/DeleteFolderModal';
import { ExportPdfModalContainer } from '../ExportPdfModal/ExportPdfModalContainer';
import { MoveFolderModal } from '../MoveFolderModal/MoveFolderModal';
import { Navigation } from '../Navigation';
import { PageHeader } from '../PageHeader';
import { RenameFolderModal } from '../RenameFolderModal';
import { SelectionToolbar } from '../SelectionToolbar';
import { Sidebar } from '../Sidebar';
import { exportPdfMessage, StoryListBanner } from '../StoryListBanner';
import { StoryListContent } from '../StoryListContent/StoryListContent';
import { StoryListContext } from '../storyListContext';

import { styles } from './StoryList.styles';
import { PageErrorBoundary } from '../../ErrorBoundary/PageErrorBoundary';
import { ViewName } from '../../../../instrumentation/views';
import { WorkspaceSidebarProvider } from '@opengov/bnp-component-workflows-widgets';

const cookies = new Cookies();

const copyFolderSubscription = new HasuraSubscription({
  query: folderCopyProcessesSubscriptionQuery,
  longPollInterval: LONG_POLL_INTERVAL_SHORT
});

const copyStoriesSubscription = new HasuraSubscription({
  query: storiesCopyProcessesSubscriptionQuery,
  longPollInterval: LONG_POLL_INTERVAL_SHORT
});

const exportPdfSubscription = new HasuraSubscription({
  query: getExportPdfDetailsForFolder
});

const SLOW_FADE_HIGHLIGHT_TIMEOUT = 7000;

const EMPTY_STORY_LISTING = { currentPage: 0, data: [], storiesUrl: null, total: 0 } as unknown as IStoryListing;

export interface IStoryListProps {
  showGlobalNavBar: boolean;
  storyMarginsEnabled: boolean;
  canCopyIndividualStories: boolean;
  canCopyStoriesFolders: boolean;
  canOrganizeStories: boolean;
  canFollowStories?: boolean;
  storiesAccessControlEnabled: boolean;
  displaySidebar: boolean;
  entityId: number;
  entityName: string;
  entitySubdomain: string;
  entityUuid: string;
  isOpenGovAdmin: boolean;
  isTemplate: boolean;
  lastPath: string;
  publicationViewMessages: IPublicationViewMessages;
  messages: IStoryListMessages;
  publicationsMessages: IPublicationListMessages;
  publicationModalMessages: IPublicationModalMessages;
  modalMessages: IModalMessages;
  canOrganizeFolders: boolean;
  user: ISessionUser;
  isFormsAndWorkflowsFeatureEnabled: boolean;
  isPerformanceManagementFeatureEnabled: boolean;
  isCustomSpreadsheetReportEnabled: boolean;
  displayPublicationTab: boolean;
  navigateToSettings(tab?: string): any;
  navigateToStoryView(entitySubdomain: string, storyId: string, isTemplate: boolean): any;
  navigateToPublicationView(entitySubdomain: string, storyId: string): any;
  navigateToCreateFromTemplate(): any;
  setSelectedList(
    newList: IStoryListSelection,
    entitySubdomain: string,
    isOrganize?: boolean,
    selectedFolderId?: string
  ): void;
  pushToOrganizeStoriesAndFolders(entitySubdomain: string, selectedFolderId: string, rootId: string): void;
}

const updateSort = (sort: ISortOptions, setSort) => {
  setSort(sort);
  cookies.set('story', getNewCookieValue(cookies.get('story'), SORT, sort));
};

const updateView = (view: IStoryViewOptions, setView) => {
  setView(view);
  cookies.set('story', getNewCookieValue(cookies.get('story'), VIEW, view));
};

const updateShow = (show: IStoryShowOptions, setShow) => {
  setShow(show);
  cookies.set('story', getNewCookieValue(cookies.get('story'), SHOW, show));
};

const updateTab = (tab, entitySubdomain, setPage, setSelectedList, clearFilters) => {
  if (tab === TAB_PUBLICATIONS) {
  } else {
    setPage(1);
  }
  clearFilters();
  setSelectedList({ id: tab, displayName: tab }, entitySubdomain);
};

const updateStoryVisibility = (storyVisibility: IStoryVisibilityOptions, setStoryVisibility) => {
  setStoryVisibility(storyVisibility);
  cookies.set('story', getNewCookieValue(cookies.get('story'), STORY_VISIBILITY, storyVisibility));
};

const updatePublicationVisibility = (publicationVisibility: IStoryVisibilityOptions, setPublicationVisibility) => {
  setPublicationVisibility(publicationVisibility);
  cookies.set('story', getNewCookieValue(cookies.get('story'), PUBLICATION_VISIBILITY, publicationVisibility));
};

const getHeaderText = (
  messages: IStoryListMessages,
  tab: string,
  show: IStoryShowOptions,
  lastPath: string,
  currentFolderName: string
): string | undefined => {
  if (lastPath === TAB_TEMPLATES) {
    return messages.allTemplates;
  }
  if (tab === TAB_STORIES) {
    if (currentFolderName) {
      return currentFolderName;
    }
    if (show === SHOW_USER) {
      return messages.yourStories;
    } else {
      return messages.allStories;
    }
  } else if (tab === TAB_TEMPLATES) {
    if (show === SHOW_USER) {
      return messages.yourTemplates;
    } else {
      return messages.allTemplates;
    }
  } else if (tab === TAB_ANALYTICS) {
    return messages.analytics;
  }
};

const getPaginationInfo = (
  lastPath: string,
  messages: IStoryListMessages,
  storyListContents: IStoryListing,
  tab: ITabOptions,
  templates: IStoryListing
) => {
  let pageData;
  let paginatedItemsName = messages.stories;
  // Set page data based on current tab
  switch (tab) {
    default:
    case TAB_STORIES:
    case TAB_ANALYTICS:
      pageData = omit(storyListContents, ['data']);
      break;
    case TAB_TEMPLATES:
      pageData = omit(templates, ['data']);
      paginatedItemsName = messages.templates;
      break;
  }

  if (lastPath === TAB_TEMPLATES) {
    pageData = omit(templates, ['data']);
    paginatedItemsName = messages.templates;
  }
  return { pageData, paginatedItemsName };
};

const StoryListInternal: React.FC<IStoryListProps> = (props) => {
  const {
    showGlobalNavBar,
    storyMarginsEnabled,
    canCopyIndividualStories,
    canCopyStoriesFolders,
    canOrganizeStories,
    canFollowStories,
    storiesAccessControlEnabled,
    displaySidebar,
    entityId,
    entityName,
    entitySubdomain,
    entityUuid,
    isOpenGovAdmin,
    isTemplate,
    lastPath,
    publicationViewMessages,
    messages,
    publicationsMessages,
    publicationModalMessages,
    modalMessages,
    canOrganizeFolders,
    user,
    isFormsAndWorkflowsFeatureEnabled,
    isPerformanceManagementFeatureEnabled,
    isCustomSpreadsheetReportEnabled,
    displayPublicationTab,
    navigateToSettings,
    navigateToStoryView,
    navigateToPublicationView,
    navigateToCreateFromTemplate,
    setSelectedList,
    pushToOrganizeStoriesAndFolders,
  } = props;

  const { tab, currentFolderId, selectedStoryList } = useStoryListRoute(messages);

  const { data: fetchedSettings } = useFetchSettings({ entityId });
  const settings = fetchedSettings ?? EMPTY_SETTINGS;

  const { data: currentSubjectClaims } = useCurrentSubjectClaims(
    entityUuid && user?.uuid ? { userUuid: user.uuid, entityUuid } : null
  );
  const isAdmin = hasPrivilege(currentSubjectClaims ?? null, STORIES_ADMIN_PRIVILEGE);

  const { data: userGroupIds = [] } = useCurrentUserGroupsIds(
    tab === TAB_PUBLICATIONS && entityId && storiesAccessControlEnabled && !isAdmin && user?.uuid && entityUuid
      ? { subjectId: user.uuid, entityUuid }
      : null
  );

  const {
    analytics,
    isAnalyticsLoading,
    setAnalyticsAuthHeader,
    getAnalyticsProperty,
    getAnalyticsData,
    toggleAnalyticsLoading
  } = useStoryListAnalytics(settings?.advanced?.google_analytics_id);

  const botConfig = {
    integrationKey: window.process.env.SF_INTEGRATION_KEY,
    platformName: window.process.env.SF_PLATFORM_NAME,
    messageId: window.process.env.SF_MESSAGE_ID,
    secretUrl: window.process.env.SF_SECRET_URL
  };

  useChatBotSnippet({
    firstName: user.first_name,
    lastName: user.last_name,
    email: user.email,
    ...botConfig
  });

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);

  const [sort, setSort] = useState<ISortOptions>(SORT_UPDATED_NEW_TO_OLD);
  const [show, setShow] = useState<IStoryShowOptions>(SHOW_ALL);
  const [view, setView] = useState(VIEW_LIST);
  const [storyVisibility, setStoryVisibility] = useState<IStoryVisibilityOptions>(STATE_ALL);
  const [publicationVisibility, setPublicationVisibility] = useState<IStoryVisibilityOptions>(STATE_ALL);
  const [search, setSearch] = useState('');

  const [selectedStories, setSelectedStories] = useState<Map<string, IStory>>(Map([]));
  const [selectAllStatus, setSelectAllStatus] = useState(ESelectAllStatus.None);

  const [highlightedStoriesIds, setHighlightedStoriesIds] = useState<string[]>([]);
  const highlightFadeTimeout = useRef<ReturnType<typeof setTimeout>>();

  const [manageFolderAnchorEl, setManageFolderAnchorEl] = useState<null | HTMLElement>(null);
  const [isMoveFolderModalVisible, setIsMoveFolderModalVisible] = useState(false);
  const [isDeleteFolderModalVisible, setIsDeleteFolderModalVisible] = useState(false);
  const [isRenameFolderModalVisible, setIsRenameFolderModalVisible] = useState(false);
  const [isCreateStoryModalVisible, setIsCreateStoryModalVisible] = useState(false);
  const [isCopyFolderModalVisible, setIsCopyFolderModalVisible] = useState(false);
  const [isOrganizeFoldersOptionVisible, setIsOrganizeFoldersOptionVisible] = useState(false);
  const [isExportPdfModalVisible, setIsExportPdfModalVisible] = useState(false);
  const publicationsListControllerRef = useRef(null);

  const [folderCopyProcess, setFolderCopyProcess] = useState<IFolderCopyProcess | null>(null);
  const [storiesCopyProcesses, setStoriesCopyProcesses] = useState<IStoriesCopyProcess[]>([]);
  const [exportPdfProcess, setExportPdfProcess] = useState<IExportPdfProcess | null>(null);
  const storyCookie = cookies.get('story') as IStoryCookie;
  const [allStoriesCount, setAllStoriesCount] = useState(0);
  const [allPublicationsList, setAllPublicationsList] = useState([]);

  const publicationQueryParams =
    tab === TAB_PUBLICATIONS && entityId > 0
      ? {
        entityId,
        page: page || 1,
        perPage: perPage || DEFAULT_PER_PAGE,
        publicationVisibility,
        ogId: user.id,
        shouldFilterByUser: show === SHOW_USER,
        sort: sort === SORT_CUSTOM ? SORT_UPDATED_NEW_TO_OLD : sort,
        title: search,
        storiesGranularAccessEnabled: storiesAccessControlEnabled,
        isAdmin,
        userGroupIds,
        subjectId: user.uuid
      }
      : null;

  const { data: publicationList, isLoading: isPublicationsLoading } = useGetAllPublications(
    publicationQueryParams,
    { title: publicationsMessages.somethingWentWrongError, body: publicationsMessages.failedToGetPublication }
  );

  const { data: storyFoldersData, refetch: refetchStoryFolders } = useStoryFolders(
    entityId > 0 ? { entityId, displayPublicationTab } : null,
    messages.somethingWentWrongError
  );
  const rawStoryFolders = storyFoldersData?.storyFolders ?? [];
  const folderStructure: IStoriesFolder[] = useMemo(
    () => alphabetizeNodes(compileNodes(rawStoryFolders[0]?.subFolders) || []),
    [rawStoryFolders]
  );
  const rootFolderId: string = rawStoryFolders[0]?.id ?? null;

  const { data: rawPublicationFolders } = usePublicationFoldersTree(
    entityId > 0 ? { entityId } : null,
    messages.somethingWentWrongError
  );
  const publicationFolders = rawPublicationFolders ?? [];
  const publicationFoldersTree: IStoriesFolder[] = useMemo(() => {
    if (!publicationFolders.length) return [];
    const roots = publicationFolders.filter(
      (f) => f.storyFolder.title.includes('root-folder') && f.storyFolder.subFolders?.length > 0
    );
    return alphabetizeNodes(compilePublicationNodes(roots) || []);
  }, [publicationFolders]);

  const currentFolderExist = !!getFolderById(folderStructure, currentFolderId);
  const currentFolderName = getDisplayNameForNodeId(currentFolderId, folderStructure);
  const currentFolderPath: IStoriesFolder[] = folderStructure && currentFolderId
    ? getPathForFolder(folderStructure, currentFolderId)
    : [];

  const addCountOfEachFoldersStory = (allStories) => {
    let totalCount = 0;
    const addStoriesCount = (stories) => {
      stories.forEach((story) => {
        totalCount += story?.stories_count?.aggregate?.count;
        if (story.subFolders && story.subFolders.length) {
          addStoriesCount(story.subFolders);
        }
      });
    };
    addStoriesCount(allStories);
    return totalCount;
  };

  const isStoriesView = lastPath === TAB_STORIES || tab === TAB_STORIES;
  const isAnalyticsView = lastPath === TAB_ANALYTICS || tab === TAB_ANALYTICS;
  const isTemplatesView = lastPath === TAB_TEMPLATES || tab === TAB_TEMPLATES;

  const storiesParams = useMemo(() => {
    if (entityId <= 0 || (!isStoriesView && !isAnalyticsView)) {
      return null;
    }
    return {
      entityId,
      page,
      perPage,
      sort: isAnalyticsView ? sort : sort === SORT_CUSTOM && !currentFolderId ? SORT_UPDATED_NEW_TO_OLD : sort,
      title: search,
      storyVisibility: isAnalyticsView ? STATE_PUBLISHED : storyVisibility,
      ogId: user.id,
      shouldFilterByUser: show === SHOW_USER,
      folderId: isAnalyticsView ? undefined : selectedStoryList?.id
    };
  }, [
    entityId,
    isStoriesView,
    isAnalyticsView,
    page,
    perPage,
    sort,
    search,
    storyVisibility,
    user.id,
    show,
    currentFolderId,
    selectedStoryList?.id
  ]);

  const templatesParams = useMemo(() => {
    if (entityId <= 0 || !isTemplatesView) {
      return null;
    }
    return {
      isOpenGovAdmin,
      entityId,
      page,
      perPage,
      shouldFilterByUser: show === SHOW_USER,
      sort: sort === SORT_CUSTOM ? SORT_UPDATED_NEW_TO_OLD : sort,
      title: search,
      ogId: user.id
    };
  }, [entityId, isTemplatesView, isOpenGovAdmin, page, perPage, show, sort, search, user.id]);

  const storiesQuery = useStories(storiesParams, messages.somethingWentWrongError);
  const templatesQuery = useTemplates(templatesParams, messages.somethingWentWrongError);
  const storyListContents = storiesQuery.data ?? EMPTY_STORY_LISTING;
  const templates = templatesQuery.data ?? EMPTY_STORY_LISTING;
  const storiesUrl = storyListContents.storiesUrl ?? templates.storiesUrl ?? null;
  const isLoading =
    (storiesParams !== null && (storiesQuery.isLoading || storiesQuery.isFetching)) ||
    (templatesParams !== null && (templatesQuery.isLoading || templatesQuery.isFetching));

  const foldersStoryCountParams = useMemo(
    () => (currentFolderId && entityId > 0 && tab === TAB_STORIES ? { entityId, folderId: currentFolderId } : null),
    [currentFolderId, entityId, tab]
  );
  const foldersStoryCountQuery = useFoldersAndSubFoldersStoryCount(
    foldersStoryCountParams,
    messages.somethingWentWrongError
  );

  const getPublicationsList = debounce(
    () => {
      if (publicationsListControllerRef.current) {
        publicationsListControllerRef.current.abort();
      }
      const controller = new AbortController();
      publicationsListControllerRef.current = controller;

      void fetchAllPublicationsInEntity(entityId, publicationsListControllerRef.current).then(
        (response) => {
          if (Array.isArray(response)) {
            setAllPublicationsList(response);
          }
        }
      );
    },
    100,
    { trailing: true }
  );

  useEffect(() => {
    if (
      tab === TAB_STORIES &&
      currentSubjectClaims &&
      storiesAccessControlEnabled &&
      !hasPrivilege(currentSubjectClaims, STORIES_ADMIN_PRIVILEGE)
    ) {
      navigateToPublicationList(entitySubdomain);
    }
  }, [
    tab,
    currentSubjectClaims,
    entitySubdomain,
    storiesAccessControlEnabled,
    navigateToPublicationList,
    hasPrivilege
  ]);

  useEffect(() => {
    const response = foldersStoryCountQuery.data;
    if (!response) {
      return;
    }

    if (!foldersStoryCountQuery.isFetching && Array.isArray(response) && response.length > 0 && !!(response as any)[0]?.publicationId) {
      setSelectedList({ id: 'stories', displayName: messages.allStories }, entitySubdomain);
    }
    setAllStoriesCount(addCountOfEachFoldersStory(response));
  }, [foldersStoryCountQuery.data, foldersStoryCountQuery.isFetching, entitySubdomain, messages.allStories, setSelectedList]);

  useEffect(() => {
    if (entityId && !allPublicationsList.length) {
      getPublicationsList();
    }
  }, [entityId]);

  useEffect(() => {
    if (storyCookie) {
      if (storyCookie.view) {
        setView(storyCookie.view);
      }
      if (!(tab === TAB_PUBLICATIONS)) {
        if (storyCookie.sort) {
          setSort(storyCookie.sort);
        }
        if (storyCookie.storyVisibility) {
          setStoryVisibility(storyCookie.storyVisibility);
        }
        if (storyCookie.show) {
          setShow(storyCookie.show);
        }
      }
    }
  }, []);

  useEffect(() => {
    if (selectedStories.size && tab !== TAB_PUBLICATIONS) {
      setSelectedStories(Map([]));
      setSelectAllStatus(ESelectAllStatus.None);
    }
  }, [props.lastPath, page, tab]);

  useEffect(() => {
    if (tab === TAB_PUBLICATIONS) return;
    const selectedFolderNode = getFolderById(folderStructure, currentFolderId) as IStoriesFolder;
    if (selectedFolderNode && selectedFolderNode.children) {
      setIsOrganizeFoldersOptionVisible(selectedFolderNode.children.length > 0);
    }
  }, [currentFolderId, folderStructure]);

  const subscribeFolderCopyProcessMessages = () => {
    const onMessageReceived: MessageListener = (messageData) => {
      const folderProcess: IFolderCopyProcess = messageData?.data?.folderProcess;
      if (folderProcess?.status === EProcessStatus.Success) {
        copyFolderSubscription.unsubscribe();
        setFolderCopyProcess(folderProcess);
        if (!folderProcess.suppressDisplayBanner) {
          // Refetch folders (react-query) after a successful copy, then navigate.
          // Replaces the retired storyListEffects.getStoryFolders effect.
          void refetchStoryFolders().then(() => {
            navigateToFolderList(entitySubdomain, folderProcess.destinationFolderId);
          });
        }
      } else if (folderProcess?.status === EProcessStatus.Error) {
        setFolderCopyProcess(folderProcess);
        copyFolderSubscription.unsubscribe();
      } else if (!folderProcess) {
        copyFolderSubscription.unsubscribe();
      } else {
        setFolderCopyProcess(folderProcess || null);
      }
    };
    copyFolderSubscription.addMessageListener(onMessageReceived);
    copyFolderSubscription.subscribe({ variables: { folderId: currentFolderId } });
  };

  const subscribeStoriesCopyProcessMessages = () => {
    const onMessageReceived: MessageListener = (messageData) => {
      const storiesProcesses: IStoriesCopyProcess[] = messageData?.data?.storiesProcesses;
      const latestStoriesProcess = storiesProcesses[0];
      if (latestStoriesProcess?.status === EProcessStatus.Success) {
        copyStoriesSubscription.unsubscribe();
        if (!latestStoriesProcess?.suppressDisplayBanner) {
          navigateToFolderList(entitySubdomain, currentFolderId);
          setHighlightedStoriesIds(latestStoriesProcess.storyCopyProcesses.map(({ storyCopyId }) => storyCopyId));
          highlightFadeTimeout.current = setTimeout(() => {
            setHighlightedStoriesIds([]);
          }, SLOW_FADE_HIGHLIGHT_TIMEOUT);
        }
      } else if (latestStoriesProcess?.status === EProcessStatus.Error) {
        copyStoriesSubscription.unsubscribe();
      } else if (!latestStoriesProcess) {
        copyStoriesSubscription.unsubscribe();
      }
      setStoriesCopyProcesses(storiesProcesses);
    };
    copyStoriesSubscription.addMessageListener(onMessageReceived);
    copyStoriesSubscription.subscribe({ variables: { folderId: currentFolderId } });
  };

  const subscribeExportPdfMessages = () => {
    const onMessageReceived: MessageListener = (messageData) => {
      const latestExportPdfProcess: IExportPdfProcess = messageData?.data?.exportFolderPdfRequests[0];
      setExportPdfProcess(latestExportPdfProcess || null);
    };
    exportPdfSubscription.addMessageListener(onMessageReceived);
    exportPdfSubscription.subscribe({ variables: { folderId: currentFolderId } });
  };

  //Folder copy process
  useEffect(() => {
    setFolderCopyProcess(null);
    if (!currentFolderId || !canCopyStoriesFolders) return;
    copyFolderSubscription.clearAllMessageListeners();
    subscribeFolderCopyProcessMessages();

    return () => {
      copyFolderSubscription.unsubscribe();
    };
  }, [currentFolderId, entityId, entitySubdomain, canCopyStoriesFolders]);

  //Stories copy process
  useEffect(() => {
    setStoriesCopyProcesses([]);
    setHighlightedStoriesIds([]);
    clearTimeout(highlightFadeTimeout.current);
    if (!currentFolderId || !canCopyIndividualStories || tab === TAB_PUBLICATIONS) return;
    copyStoriesSubscription.clearAllMessageListeners();
    subscribeStoriesCopyProcessMessages();

    return () => {
      copyStoriesSubscription.unsubscribe();
    };
  }, [currentFolderId, entityId, entitySubdomain, canCopyIndividualStories, tab]);

  //Export PDF effect
  useEffect(() => {
    setExportPdfProcess(null);
    if (!currentFolderId || tab !== TAB_STORIES) return;
    // Listen for messages
    exportPdfSubscription.clearAllMessageListeners();
    subscribeExportPdfMessages();

    return () => {
      exportPdfSubscription.unsubscribe();
    };
  }, [canOrganizeFolders, currentFolderId, entityId, entitySubdomain, tab]);

  const isLastFolder = isTopLevelNode(folderStructure, currentFolderId) && folderStructure.length === 1;
  const deleteFolderDisabled = isLastFolder;

  const headerText = getHeaderText(messages, tab, show, lastPath, currentFolderName);

  const { pageData, paginatedItemsName } = getPaginationInfo(lastPath, messages, storyListContents, tab, templates);

  const { currentPage, total } = pageData;
  const hasPendingCopyFolderProcess = currentFolderId && folderCopyProcess?.status === EProcessStatus.InProgress;
  const hasPendingCopyStoriesProcess =
    currentFolderId && !!storiesCopyProcesses.find((process) => process.status === EProcessStatus.InProgress);
  const showPagination =
    (tab !== TAB_ANALYTICS || !!analytics.authHeader) &&
    !!currentPage &&
    !hasPendingCopyFolderProcess &&
    !isLoading &&
    total > 0;

  const renderModals = () => {
    const copyFolderModal =
      isCopyFolderModalVisible &&
      canCopyStoriesFolders &&
      createPortal(
        <CopyFolderModal
          currentFolderId={currentFolderId}
          currentFolderName={currentFolderName}
          folderToCopy={{ id: currentFolderId, displayName: currentFolderName }}
          folderStructure={folderStructure}
          publicationFolders={publicationFolders}
          publicationViewMessages={publicationViewMessages}
          publicationFolderStructure={publicationFoldersTree}
          messages={messages}
          entityId={entityId}
          entitySubdomain={entitySubdomain}
          rootFolderId={rootFolderId}
          userId={user.id}
          onClose={() => setIsCopyFolderModalVisible(false)}
        />,
        document.getElementById('app')
      );
    const moveFolderModal =
      isMoveFolderModalVisible &&
      canOrganizeStories &&
      createPortal(
        <MoveFolderModal
          folderToMove={{ id: currentFolderId, displayName: currentFolderName }}
          folderStructure={folderStructure}
          messages={messages}
          entityId={entityId}
          rootFolderId={rootFolderId}
          onClose={() => setIsMoveFolderModalVisible(false)}
        />,
        document.getElementById('app')
      );
    const deleteFolderModal =
      isDeleteFolderModalVisible &&
      createPortal(
        <DeleteFolderModal
          currentFolderId={currentFolderId}
          entityId={entityId}
          currentFolderStoryCount={total}
          folderStructure={folderStructure}
          messages={messages}
          entitySubdomain={entitySubdomain}
          onClose={() => setIsDeleteFolderModalVisible(false)}
          setSelectedList={setSelectedList}
        />,
        document.getElementById('app')
      );
    const renameFolderModal =
      isRenameFolderModalVisible &&
      createPortal(
        <RenameFolderModal
          currentFolderId={currentFolderId}
          currentFolderName={currentFolderName}
          entityId={entityId}
          folderStructure={folderStructure}
          messages={messages}
          rootFolderId={rootFolderId}
          onClose={() => setIsRenameFolderModalVisible(false)}
        />,
        document.getElementById('app')
      );
    const exportPdfModal =
      isExportPdfModalVisible &&
      createPortal(
        <ExportPdfModalContainer
          folderId={currentFolderId}
          messages={modalMessages}
          storyMarginsEnabled={storyMarginsEnabled}
          entityId={entityId}
          entitySubdomain={entitySubdomain}
          pdfGenerateFailed={messages.pdfGenerateFailed}
          pdfGenerateFailedDescrption={messages.pdfGenerateFailedDescrption}
          exportFolderPdfFailed={messages.exportFolderPdfFailed}
          onClose={() => setIsExportPdfModalVisible(false)}
        />,
        document.getElementById('app')
      );
    return [copyFolderModal, moveFolderModal, deleteFolderModal, renameFolderModal, exportPdfModal];
  };

  const exportPdfInlineMesaages = () => {
    if (!currentFolderExist || !exportPdfProcess?.suppressDisplayBanner) return null;

    if (exportPdfProcess.status === EProcessStatus.InProgress) {
      return (
        <Box sx={styles.exportPdfMessageRow}>
          <Box sx={styles.spinner} data-test="export-pdf-in-progress-message">
            <span>{messages.buildingThePDF}</span>
            <CircularProgress size={16} data-test="loading-spinner" />
          </Box>
        </Box>
      );
    }

    if (exportPdfProcess.status === EProcessStatus.Success) {
      return (
        <Box sx={styles.exportPdfMessageRow}>
          <Box sx={styles.pdfDownloadMessage}>
            <CheckCircleOutlineIcon color="success" fontSize="small" />
            {exportPdfMessage(exportPdfProcess.createdAt, messages, exportPdfProcess.id)}
          </Box>
        </Box>
      );
    }

    return null;
  };

  const updateShowPublication = (showValue) => {
    updateShow(showValue, setShow);
  };

  const updateSortPublication = (sortValue) => {
    updateSort(sortValue, setSort);
  };

  const updateViewPublication = (viewValue) => {
    updateView(viewValue, setView);
  };

  const updateVisibility = (viewValue) => {
    updatePublicationVisibility(viewValue, setPublicationVisibility);
  };

  const updateSearchPublication = (searchValue) => {
    setSearch(searchValue);
  };

  const resetFilters = () => {
    setSearch('');
    setShow(SHOW_ALL);
    setStoryVisibility(STATE_ALL);
    setPublicationVisibility(STATE_ALL);
  };

  return (
    <StoryListContext.Provider
      value={{
        highlightedStoriesIds,
        setHighlightedStoriesIds,
        selectedStories,
        setSelectedStories,
        selectAllStatus,
        setSelectAllStatus
      }}
    >
      {renderModals()}
      <Box sx={styles.root} data-test="story-gallery-view">
        <WorkspaceSidebarProvider
          entitySubdomain={props.entitySubdomain}
          entityUuid={props.entityUuid}
          recentActivitiesSource="stories"
          formsAndWorkflowsFeatureEnabled={isFormsAndWorkflowsFeatureEnabled}
          performanceManagementFeatureEnabled={isPerformanceManagementFeatureEnabled}
        >
          <PageHeader
            publicationModalMessages={publicationModalMessages}
            publicationsCount={publicationList?.total || 0}
            displayPublicationTab={displayPublicationTab}
            isPublicationTabSelected={tab === TAB_PUBLICATIONS}
            canOrganizeStories={canOrganizeStories}
            currentFolderId={currentFolderId}
            entityId={entityId}
            entityName={entityName}
            entitySubdomain={entitySubdomain}
            folderStructure={folderStructure}
            isAdmin={isAdmin}
            isCreateStoryModalVisible={isCreateStoryModalVisible}
            isTemplate={isTemplate}
            messages={messages}
            navigateToSettings={navigateToSettings}
            navigateToCreateFromTemplate={navigateToCreateFromTemplate}
            setIsCreateStoryModalVisible={setIsCreateStoryModalVisible}
            settings={settings}
            user={user}
            search={search}
            publicationList={allPublicationsList}
          />
          <>
            {lastPath !== PATH_CREATE_FROM_TEMPLATE && (
              <Navigation
                canOrganizeStories={canOrganizeStories}
                isAdmin={isAdmin}
                tab={tab}
                updateTab={(newTab) => updateTab(newTab, entitySubdomain, setPage, setSelectedList, resetFilters)}
                messages={messages}
                settings={settings}
                displayPublicationTab={displayPublicationTab}
                hideStoriesTab={
                  currentSubjectClaims &&
                  storiesAccessControlEnabled &&
                  !hasPrivilege(currentSubjectClaims, STORIES_ADMIN_PRIVILEGE)
                }
              />
            )}

            {tab === TAB_PUBLICATIONS ? (
              <Box id="mainContent" sx={styles.publicationContainer} data-test="publications-view">
                <PublicationList
                  lastPath={lastPath}
                  storyMessages={messages}
                  publicationModalMessages={publicationModalMessages}
                  rowData={publicationList?.data ?? []}
                  isAdmin={isAdmin}
                  entityId={entityId}
                  entityUuid={props.entityUuid}
                  messages={publicationsMessages}
                  entitySubdomain={entitySubdomain}
                  folderStructure={folderStructure}
                  entityName={entityName}
                  user={user}
                  view={view}
                  isPublicationsLoading={isPublicationsLoading}
                  search={search}
                  show={show}
                  sort={sort}
                  tab={tab}
                  updateSearch={updateSearchPublication}
                  updateShow={updateShowPublication}
                  updateSort={updateSortPublication}
                  updateView={updateViewPublication}
                  publicationVisibility={publicationVisibility}
                  updatePublicationVisibility={updateVisibility}
                  clearSearchFilters={resetFilters}
                  navigateToPublicationView={navigateToPublicationView}
                  publicationTitlesList={allPublicationsList}
                  currentSubjectClaims={currentSubjectClaims}
                  isCustomSpreadsheetReportEnabled={isCustomSpreadsheetReportEnabled}
                  totalRows={publicationList?.total ?? 0}
                  page={page - 1}
                  rowsPerPage={perPage}
                  onPageChange={(newPage) => setPage(newPage + 1)}
                  onRowsPerPageChange={setPerPage}
                />
              </Box>
            ) : (
              !(
                tab === TAB_STORIES &&
                currentSubjectClaims &&
                storiesAccessControlEnabled &&
                !hasPrivilege(currentSubjectClaims, STORIES_ADMIN_PRIVILEGE)
              ) && (
                <Box sx={styles.contentWrapper}>
                  {displaySidebar && lastPath !== PATH_CREATE_FROM_TEMPLATE && tab !== TAB_ANALYTICS && (
                    <Sidebar
                      currentFolderId={currentFolderId}
                      entityId={entityId}
                      folderStructure={folderStructure}
                      isAdmin={isAdmin}
                      messages={messages}
                      rootFolderId={rootFolderId}
                      selectedList={selectedStoryList}
                      entitySubdomain={entitySubdomain}
                      setSelectedList={setSelectedList}
                    />
                  )}
                  <Box id="mainContent" sx={styles.content}>
                    {canOrganizeStories && currentFolderId && (
                      <Box sx={styles.breadcrumbsWrapper} data-test="folder-breadcrumb">
                        <Breadcrumbs aria-label="folder breadcrumb">
                          {!!currentFolderPath.length &&
                            currentFolderPath.map(({ id, displayName }) => (
                              id === currentFolderId ? (
                                <Typography key={id} color="text.primary" variant="body2" data-test="folder-breadcrumb-item">{displayName}</Typography>
                              ) : (
                                <Link
                                  key={id}
                                  component="button"
                                  variant="body2"
                                  onClick={() => setSelectedList({ id, displayName }, entitySubdomain)}
                                  data-test="folder-breadcrumb-item"
                                >
                                  {displayName}
                                </Link>
                              )
                            ))}
                        </Breadcrumbs>
                      </Box>
                    )}
                    <StoryListBanner
                      isAdmin={isAdmin}
                      messages={messages}
                      folderCopyProcess={folderCopyProcess}
                      storiesCopyProcesses={storiesCopyProcesses}
                      exportPdfProcess={exportPdfProcess}
                    />
                    <Box sx={styles.headerContainer}>
                      <div>
                        <Typography variant="h3" component="h2">
                          {headerText}
                        </Typography>
                        {currentFolderId && (
                          <Typography variant="caption" color="text.secondary" data-test="stories-count-text">
                            {`Number of stories in the selected folder and its sub-folders: ${allStoriesCount}`}
                          </Typography>
                        )}
                      </div>
                      <Box sx={styles.buttonContainer}>
                        {canOrganizeFolders && currentFolderExist && (
                          <Tooltip
                            title={exportPdfProcess?.status !== EProcessStatus.InProgress ? '' : messages.exportingPdfTooltip}
                            placement="bottom"
                            arrow
                          >
                            <span>
                              <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                data-test="export-pdf-button"
                                onClick={() => navigateToSectionSetupPDF(entitySubdomain, currentFolderId)}
                                disabled={
                                  hasPendingCopyFolderProcess ||
                                  hasPendingCopyStoriesProcess ||
                                  exportPdfProcess?.status === EProcessStatus.InProgress
                                }
                              >
                                {messages.exportPdf}
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        {isAdmin && canOrganizeStories && currentFolderExist && (
                          <>
                            <Button
                              variant="outlined"
                              color="secondary"
                              size="small"
                              data-test="manage-folder-dropdown"
                              disabled={hasPendingCopyFolderProcess || hasPendingCopyStoriesProcess}
                              onClick={(e) => setManageFolderAnchorEl(e.currentTarget)}
                              endIcon={<ArrowDropDownIcon />}
                            >
                              Manage
                            </Button>
                            <Menu
                              anchorEl={manageFolderAnchorEl}
                              open={Boolean(manageFolderAnchorEl)}
                              onClose={() => setManageFolderAnchorEl(null)}
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            >
                              <MenuItem
                                data-test="move-folder-button"
                                disabled={currentFolderId === null}
                                onClick={() => { setManageFolderAnchorEl(null); setIsMoveFolderModalVisible(true); }}
                              >
                                {messages.move}
                              </MenuItem>
                              <MenuItem
                                data-test="rename-folder-button"
                                onClick={() => { setManageFolderAnchorEl(null); setIsRenameFolderModalVisible(true); }}
                              >
                                {messages.rename}
                              </MenuItem>
                              {canCopyStoriesFolders && (
                                <MenuItem
                                  data-test="copy-folder-button"
                                  onClick={() => { setManageFolderAnchorEl(null); setIsCopyFolderModalVisible(true); }}
                                >
                                  {messages.copyFolder}
                                </MenuItem>
                              )}
                              {canOrganizeFolders && (isOrganizeFoldersOptionVisible || total > 0) && (
                                <MenuItem
                                  data-test="organize-folders-button"
                                  onClick={() => pushToOrganizeStoriesAndFolders(entitySubdomain, currentFolderId, currentFolderId)}
                                >
                                  {messages.organizeStoriesAndFolders}
                                </MenuItem>
                              )}
                              <Tooltip title={deleteFolderDisabled ? messages.mustHaveFolder : ''} placement="top-end" arrow>
                                <span>
                                  <MenuItem
                                    data-test="delete-folder-button"
                                    disabled={deleteFolderDisabled}
                                    sx={{ color: 'error.main' }}
                                    onClick={() => { setManageFolderAnchorEl(null); setIsDeleteFolderModalVisible(true); }}
                                    component="button"
                                  >
                                    {messages.delete}
                                  </MenuItem>
                                </span>
                              </Tooltip>
                            </Menu>
                          </>
                        )}
                      </Box>
                    </Box>
                    {exportPdfInlineMesaages()}
                    {selectedStories.size > 0 ? (
                      <SelectionToolbar
                        entityId={entityId}
                        entitySubdomain={entitySubdomain}
                        canCopyIndividualStories={canCopyIndividualStories}
                        currentFolderId={currentFolderId}
                        folderStructure={folderStructure}
                        publicationFoldersTree={publicationFoldersTree}
                        messages={messages}
                        userId={user.id}
                        canFollowStories={canFollowStories}
                        onStoriesRefresh={() => void storiesQuery.refetch()}
                      />
                    ) : (
                      <ControlBar
                        lastPath={lastPath}
                        messages={messages}
                        search={search}
                        show={show}
                        sort={sort}
                        currentFolderId={currentFolderId}
                        storyVisibility={storyVisibility}
                        tab={tab}
                        view={view}
                        updateSearch={setSearch}
                        updateShow={(newShow) => updateShow(newShow, setShow)}
                        updateSort={(newSort) => updateSort(newSort, setSort)}
                        updateStoryVisibility={(newVisibility) =>
                          updateStoryVisibility(newVisibility, setStoryVisibility)
                        }
                        updateView={(newView) => updateView(newView, setView)}
                      />
                    )}
                    <StoryListContent
                      showGlobalNavBar={showGlobalNavBar}
                      analytics={analytics}
                      canOrganizeStories={canOrganizeStories}
                      clearSearchFilters={resetFilters}
                      currentFolderId={currentFolderId}
                      displayCreateStoryModal={() => setIsCreateStoryModalVisible(true)}
                      entityId={entityId}
                      entityName={entityName}
                      entitySubdomain={entitySubdomain}
                      folderStructure={folderStructure}
                      hasPendingCopyFolderProcess={hasPendingCopyFolderProcess}
                      getAnalyticsData={getAnalyticsData}
                      getAnalyticsProperty={getAnalyticsProperty}
                      isAdmin={isAdmin}
                      isAnalyticsLoading={isAnalyticsLoading}
                      isLoading={isLoading}
                      isOpenGovAdmin={isOpenGovAdmin}
                      isTemplate={isTemplate}
                      isViewingFolder={!!currentFolderId}
                      lastPath={lastPath}
                      messages={messages}
                      navigateToAllStoriesList={() => {
                        setSelectedList({ id: 'stories', displayName: messages.allStories }, entitySubdomain);
                      }}
                      navigateToSettings={navigateToSettings}
                      navigateToStoryView={navigateToStoryView}
                      search={search}
                      setAnalyticsAuthHeader={({ authHeader }) => setAnalyticsAuthHeader(authHeader)}
                      settings={settings}
                      show={show}
                      stories={storyListContents}
                      storiesUrl={storiesUrl}
                      storyVisibility={storyVisibility}
                      tab={tab}
                      templates={templates}
                      toggleAnalyticsLoading={toggleAnalyticsLoading}
                      user={user}
                      view={view}
                      key={folderStructure.toString()}
                      setSelectedList={setSelectedList}
                      publicationsList={allPublicationsList}
                      paginationOptions={showPagination ? {
                        totalRows: total,
                        rowsPerPage: perPage,
                        setRowsPerPage: setPerPage,
                        currentPage,
                        setPage,
                        itemName: paginatedItemsName,
                      } : undefined}
                    />
                  </Box>
                </Box>
              )
            )}
          </>
          <Alert
            effect="scale"
            position="bottom-right"
            timeout={5000}
            offset={25}
            stack={{
              limit: 3,
              spacing: 25
            }}
            contentTemplate={Flash}
          />
        </WorkspaceSidebarProvider>
      </Box>
    </StoryListContext.Provider>
  );
};

export const StoryList: React.FC<IStoryListProps> = (props) => {
  return (
    <PageErrorBoundary viewName={ViewName.StoriesAndPublicationAndTemplateListPage}>
      <StoryListInternal {...props} />
    </PageErrorBoundary>
  );
};
